import CaplyMark from "./CaplyMark";
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import TrialBanner from './TrialBanner';
import { useBilling } from '../context/BillingContext';
import { useAuth } from '../context/AuthContext';
import api from '../config';
import {
  Briefcase, PlusCircle, FileText, Settings, LogOut, Menu, X, BookOpen, Scale, Activity, BookMarked, ShieldCheck,
  CreditCard,
  Sparkles, Send, Loader2, RotateCcw, Target, Bell,
} from 'lucide-react';

// Portal docs always open on the branded domain, even if the app itself is
// accessed via its azurecontainerapps address.
const PORTAL_BASE = typeof window !== 'undefined' && window.location.hostname.endsWith('caply4pe.com')
  ? ''
  : 'https://www.caply4pe.com';
const USER_GUIDE_URL = `${PORTAL_BASE}/user-guide.html?v=25`;
const GLOSSARY_URL = `${PORTAL_BASE}/glossary.html?v=2`;
const TERMS_URL = `${PORTAL_BASE}/terms.html`;
const PRIVACY_URL = `${PORTAL_BASE}/privacy.html`;

// Plain-language navigation, grouped the way a PE team actually works:
// first the companies you own, then the deal hunt. No jargon.
const navGroups = [
  {
    heading: 'Current Portfolio',
    items: [
      { name: 'Current Portfolio',    path: '/portfolio',      icon: Briefcase },
      { name: 'Alerts',              path: '/monitoring',     icon: Bell },
      { name: 'Standardize Financials', path: '/normalization', icon: Scale },
    ],
  },
  {
    heading: 'Deal Flow',
    items: [
      { name: 'Find Deals',          path: '/sourcing',       icon: Target },
      { name: 'Add a Deal',          path: '/new-deal',       icon: PlusCircle },
      { name: 'Active Deals',         path: '/reports',        icon: FileText },
    ],
  },
];

// ── Global "Ask Caply" assistant: tenant-wide grounded Q&A, available on every page ──
function AskCaplyPanel({ open, onClose, autoAsk }) {
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Keep the latest message in view: scroll to the bottom whenever a new
  // message arrives or the assistant finishes typing.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const clearChat = () => {
    if (busy) return;
    setMessages([]);
    setQuestion('');
  };

  // A question handed in from outside (e.g. the welcome box) is sent
  // exactly once, when the drawer opens with it.
  const sentAutoRef = useRef(null);
  useEffect(() => {
    if (open && autoAsk && sentAutoRef.current !== autoAsk) {
      sentAutoRef.current = autoAsk;
      ask(autoAsk);
    }
  }, [open, autoAsk]);

  const suggestions = [
    "Summarize my portfolio performance",
    "Which company has the strongest EBITDA margin?",
    "What deals are in my pipeline?",
  ];

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || busy) return;
    setQuestion('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const history = messages.slice(-4).map(m =>
        m.role === 'user' ? { question: m.text } : { answer: m.text }
      );
      const resp = await api.post('/api/agents/concierge/chat', {
        message: q,
        context: { history },
      });
      const followUps = resp.data?.metadata?.follow_ups || [];
      setMessages(prev => [...prev, { role: 'assistant', text: resp.data.response || resp.data, followUps }]);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setMessages(prev => [...prev, { role: 'assistant', text: `Sorry, that didn't work — ${detail}` }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-navy-950/30 z-40" onClick={onClose} />
      <aside className="fixed top-0 right-0 bottom-0 w-full sm:w-96 bg-white border-l border-slate-200 shadow-xl z-50 flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-navy-900">Ask Caply AI</h3>
            <p className="text-[11px] text-slate-500">Grounded in your firm's portfolio &amp; deals</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={clearChat}
              disabled={busy || messages.length === 0}
              title="Clear chat and start over"
              className="p-1.5 text-slate-400 hover:text-brand-700 hover:bg-slate-100 rounded-lg disabled:opacity-40 disabled:hover:text-slate-400 disabled:hover:bg-transparent transition"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <p className="px-5 pt-3 text-[11px] text-slate-500">
          Answers come only from your firm's own data in Caply. Ask "what" and "why" about
          portfolio companies, pipeline deals, and metrics — Caply never invents figures.
        </p>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {suggestions.map(sg => (
                <button
                  key={sg}
                  onClick={() => ask(sg)}
                  disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-full border border-brand-200 text-brand-700 bg-brand-50 hover:bg-brand-100 transition disabled:opacity-50"
                >
                  {sg}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <React.Fragment key={i}>
              <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-brand-600 text-navy-950'
                    : 'bg-slate-100 text-slate-700 border border-slate-200'
                }`}>
                  {m.text}
                </div>
              </div>
              {m.role === 'assistant' && m.followUps?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-0.5 pb-1">
                  {m.followUps.map(fq => (
                    <button
                      key={fq}
                      onClick={() => ask(fq)}
                      disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-full border border-brand-200 text-brand-700 bg-brand-50 hover:bg-brand-100 transition disabled:opacity-50"
                    >
                      {fq}
                    </button>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="rounded-xl px-3.5 py-2 bg-slate-100 border border-slate-200 flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading your firm's data…
              </div>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-4 border-t border-slate-200 flex items-center gap-2">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about your portfolio or deals…"
            disabled={busy}
            className="flex-1 text-sm rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none disabled:opacity-60"
          />
          <button type="submit" disabled={busy || !question.trim()} className="btn-primary text-sm disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </aside>
    </>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [autoAsk, setAutoAsk] = useState(null);

  // Any component can open the Ask Caply drawer with a ready-to-send
  // question via window.dispatchEvent('caply:ask').
  useEffect(() => {
    const handler = (e) => {
      setAutoAsk(e.detail?.question || null);
      setAskOpen(true);
    };
    window.addEventListener('caply:ask', handler);
    return () => window.removeEventListener('caply:ask', handler);
  }, []);
  const { user, logout } = useAuth();
  const { billing } = useBilling();
  // The self-service note belongs to a workspace that is still deciding
  // whether to keep Caply. Once it is comped or paying, the note is wrong.
  const workspaceNoteKey = `caply_ws_note_${user?.tenant_id || 'workspace'}`;
  const [workspaceBannerHidden, setWorkspaceBannerHidden] = useState(
    () => localStorage.getItem(workspaceNoteKey) === '1'
  );
  const inPersonalWorkspace = !!user?.is_personal_workspace
    && billing?.status === 'trialing'
    && !workspaceBannerHidden;
  // Public demo workspaces are read only: say so up front instead of letting
  // a visitor discover it when a button refuses them.
  const inDemoWorkspace = billing?.plan_id === 'demo';

  // One shared notice when the read-only demo refuses a change.
  const [demoNotice, setDemoNotice] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      setDemoNotice(e.detail?.message || 'The demo workspace is read only.');
      setTimeout(() => setDemoNotice(null), 7000);
    };
    window.addEventListener('caply:demo', handler);
    return () => window.removeEventListener('caply:demo', handler);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();

  // The header shows what a person recognises: their workspace and their role.
  // Role codes stay internal (Caply_Admin -> Admin) and tenant ids never appear.
  const roleLabel = (role) => {
    if (role === 'Caply_SuperAdmin') return 'Platform staff';
    if (role === 'Caply_Admin' || role === 'Admin') return 'Admin';
    if (role === 'Caply_Analyst' || role === 'Analyst') return 'Analyst';
    if (role === 'Caply_Viewer' || role === 'Viewer') return 'Viewer';
    return role || 'Member';
  };
  const workspaceLabel = user?.workspace_name || user?.tenant || '';

  const getPageTitle = (path) => {
    if (path.startsWith('/billing')) return 'Plan & Billing';
    if (path.startsWith('/platform')) return 'Platform console';
    const item = navGroups.flatMap(g => g.items).find(n => path.startsWith(n.path));
    return item ? item.name : 'Current Portfolio';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-navy-950/40 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform duration-200 ease-in-out md:static md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="px-5 py-5 flex items-center gap-3 border-b border-slate-200">
          <CaplyMark className="w-11 h-11 shrink-0" />
          <div>
            <div className="font-extrabold text-xl text-navy-900 tracking-tight leading-none">Caply</div>
            <div className="text-[10px] font-medium text-slate-500 mt-1 uppercase tracking-widest">PE Intelligence</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.heading} className="mb-4">
              <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                {group.heading}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-all duration-150 ${
                          isActive
                            ? 'bg-brand-100 text-navy-900 border-l-2 border-brand-500'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-navy-900 border-l-2 border-transparent'
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span>{item.name}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="space-y-0.5 pt-2 border-t border-slate-200">
            <NavLink
              to="/billing"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-brand-100 text-navy-900 border-l-2 border-brand-500'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-navy-900 border-l-2 border-transparent'
                }`
              }
            >
              <CreditCard className="w-4 h-4 shrink-0" />
              <span>Plan &amp; Billing</span>
            </NavLink>

            {user?.is_super_admin && (
              <NavLink
                to="/platform"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-all duration-150 ${
                    isActive
                      ? 'bg-navy-900 text-brand-400 border-l-2 border-brand-500'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-navy-900 border-l-2 border-transparent'
                  }`
                }
              >
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>Platform console</span>
              </NavLink>
            )}

            <NavLink
              to="/settings"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-brand-100 text-navy-900 border-l-2 border-brand-500'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-navy-900 border-l-2 border-transparent'
                }`
              }
            >
              <Settings className="w-4 h-4 shrink-0" />
              <span>Settings</span>
            </NavLink>

            <a
              href={USER_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm text-slate-600 hover:bg-slate-100 hover:text-navy-900 border-l-2 border-transparent transition-all duration-150 mt-3"
          >
            <BookOpen className="w-4 h-4 shrink-0" />
            <span>User Guide</span>
          </a>

          <a
            href={GLOSSARY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm text-slate-600 hover:bg-slate-100 hover:text-navy-900 border-l-2 border-transparent transition-all duration-150"
          >
            <BookMarked className="w-4 h-4 shrink-0" />
            <span>PE Glossary</span>
          </a>

          <div className="pt-3 mt-3 border-t border-slate-200 space-y-2">
            <div className="flex items-center gap-3 px-3">
              <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-800 font-bold flex items-center justify-center border border-brand-200 text-sm shrink-0">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-navy-900 truncate">{user?.name || 'User'}</p>
                <p className="text-[11px] text-slate-500 truncate">{user?.email || 'user@pe.com'}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full py-2 px-3 bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-600 rounded-lg text-xs font-medium transition flex items-center justify-center gap-2 border border-slate-200 hover:border-rose-300"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Logout</span>
            </button>
          </div>
          </div>
        </nav>

      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-3.5 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-1.5 text-slate-600 hover:text-slate-900"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <h1 className="text-lg font-bold text-navy-900">{getPageTitle(location.pathname)}</h1>
          </div>
          <div className="flex items-center gap-3">
            {!inDemoWorkspace && (
              <button
                onClick={() => setAskOpen(true)}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-600 text-navy-950 text-xs font-semibold hover:bg-brand-700 transition shadow-sm"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Ask Caply</span>
              </button>
            )}
            <span className="badge badge-blue">{roleLabel(user?.role)}</span>
            {workspaceLabel && (
              <span className="hidden sm:inline-flex items-center gap-1.5 badge badge-slate" title="Your workspace">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {workspaceLabel}
              </span>
            )}
          </div>
        </header>

        {inPersonalWorkspace && (
          <div className="flex items-center gap-2.5 px-6 py-2 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm">
            <span className="text-base leading-none">&#9432;</span>
            <p className="flex-1">
              You&apos;re in your own <b>Caply workspace</b>, with sample data to explore,
              and you are its admin. It stays yours until a firm admin invites you into the
              firm&apos;s shared workspace (Settings &rarr; Team &amp; Access), which then
              takes over.
            </p>
            <button
              onClick={() => {
                localStorage.setItem(workspaceNoteKey, '1');
                setWorkspaceBannerHidden(true);
              }}
              className="p-1 rounded hover:bg-amber-100 text-amber-700"
              aria-label="Dismiss workspace notice"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {inDemoWorkspace && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-6 py-2.5 bg-navy-900 text-white text-sm">
            <span className="inline-flex items-center gap-2 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
              Read-only demo
            </span>
            <p className="flex-1 min-w-[240px] text-navy-100">
              Sample data from two fictional firms. Everything is viewable, nothing can be
              created, uploaded, sent or deleted here.
            </p>
            <Link
              to="/login?signup=1"
              className="px-3 py-1.5 rounded-md bg-brand-500 text-navy-950 text-xs font-semibold hover:bg-brand-400 transition"
            >
              Start your own workspace
            </Link>
          </div>
        )}

        <main className="flex-1 p-6 overflow-y-auto bg-slate-50 flex flex-col">
          <TrialBanner />
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>

          {/* Footer: the legal pages have to be reachable from inside the app,
              not only from the marketing site. */}
          <footer className="mt-auto pt-8">
            <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 text-[11.5px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
              <p>
                © 2026 Caply · Caply is a software platform, not an investment adviser. Nothing here is investment advice.
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-navy-900 hover:underline">Terms of Use</a>
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-navy-900 hover:underline">Privacy Policy</a>
                <a href={USER_GUIDE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-navy-900 hover:underline">User Guide</a>
                <a href="mailto:support@caply4pe.com" className="hover:text-navy-900 hover:underline">support@caply4pe.com</a>
              </div>
            </div>
          </footer>
        </main>

        {demoNotice && (
          <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 max-w-lg px-4 py-3 rounded-lg bg-navy-900 text-white text-sm shadow-lg">
            {demoNotice}
          </div>
        )}

        <AskCaplyPanel open={askOpen} onClose={() => setAskOpen(false)} autoAsk={autoAsk} />
      </div>
    </div>
  );
}
